import { useState, KeyboardEvent as ReactKeyEvent } from 'react'

import Styles from './InviteTeam.module.scss'
import cx from 'classnames'

import { Workspace } from 'types'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import client from 'api/client'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'

import { INVITE_MEMBER_EVENT, trackEvent } from 'utils/trackingUtils'
import { useAuth } from 'hooks/UseAuth'
import { useMemberProfile } from 'hooks/useMemberProfile'
import { useModals } from 'almanac-ui/Modal'
import { ConfirmPriceIncreaseModal } from 'components/ConfirmPriceIncreaseModal/ConfirmPriceIncreaseModal'
import { ConfirmPriceIncreaseDrawer } from 'components/ConfirmPriceIncreaseModal/ConfirmPriceIncreaseDrawer'
import { usePaymentPlans } from 'hooks/usePaymentPlans'
import { useCurrentPlan } from 'hooks/useCurrentPlan'
import { PillsInput } from 'components/PillsInput/PillsInput'
import { Heading } from 'almanac-ui/Heading/Heading'
import { TextAreaField } from 'components/Form/TextAreaField'
import { uniq } from 'lodash'
import { isEmail } from 'validator'
import { Plugin } from 'prosemirror-state'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useRootStore } from 'stores/RootStore'
import { Button } from 'blaze-ui/Button'
import { ArrowRight } from 'blaze-ui/icons'
import { useTierUsageLimits } from 'hooks/useTierUsageLimits'
import { PricePill } from 'blaze-ui/Pricing/PricePill/PricePill'
import { TierUpgradeModal } from 'components/TierUpgradeModal/TierUpgradeModal'
import { useCreateCardDismissal } from 'hooks/queries/useCreateCardDismissal'
import { FreemiumTooltipped } from 'components/AutopilotUpgradeButtons'
import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { LockOpen3 } from 'blaze-ui/icons/24/LockOpen3'
import { AutopilotUpgradeEntryModal } from 'components/AutopilotUpgradeEntryModal/AutopilotUpgradeEntryModal'

type Props = {
  onClose: () => void
  workspace: Workspace
}

const InviteByEmail = ({ onClose, workspace }: Props) => {
  const [emailAddressesList, setEmailAddressesList] = useState<string[]>([])
  const [message, setMessage] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)
  const { openModal } = useModals()
  const { paymentPlans } = usePaymentPlans()
  const { data: currentPlan } = useCurrentPlan()
  const { isMobile } = useResponsiveDesign()
  const { drawersStore } = useRootStore()
  const { atLimit, nextTierPlan, currentTierPlan } = useTierUsageLimits({
    limitKey: 'seats',
  })
  const { mutate: createCardDismissal } = useCreateCardDismissal()

  const shouldEnforceSeatLimit = atLimit && nextTierPlan && currentTierPlan

  const isFreeOrStarterPlan =
    currentPlan?.plan === PaymentPlanKey.Free || currentPlan?.plan === PaymentPlanKey.BlazeAutopilotStarter || currentPlan?.status === 'canceled'
  const showUpgradeToGrowthCta = isFreeOrStarterPlan

  const auth = useAuth()
  const { profile } = useMemberProfile(workspace?.id, auth?.id)


  const handleLimitWrappedPress = () => {
    if (showUpgradeToGrowthCta) {
      openModal(AutopilotUpgradeEntryModal, {
        variant: 'invite_team',
        targetPlanKey: PaymentPlanKey.BlazeAutopilotGrowth,
      })
    } else if (shouldEnforceSeatLimit) {
      openModal(TierUpgradeModal, {
        reason: 'limit_reached',
        feature: 'Seats',
        upgradeTier: nextTierPlan,
        currentTier: currentTierPlan,
        onUpgrade: () => handlePress(true),
      })
    } else {
      handlePress(false)
    }
  }

  const handlePress = (disablePriceIncreaseModal: boolean) => {
    const plan = paymentPlans.find((p) => p.key === currentPlan?.plan)
    const newMemberCount = emailAddressesList.length
    const isPriceIncrease = plan?.member_limit && workspace.members_count + newMemberCount > plan.member_limit

    if (currentPlan && isPriceIncrease && !workspace.early_adopter) {
      if (isMobile) {
        drawersStore.openDrawer(ConfirmPriceIncreaseDrawer, {
          currentPlan: currentPlan.plan,
          onConfirm: inviteTeamMembers,
          newMemberCount,
          isBillingMonthly: currentPlan.billing_frequency === 'month',
        })

        return
      }

      if (!disablePriceIncreaseModal) {
        openModal(ConfirmPriceIncreaseModal, {
          currentPlan: currentPlan.plan,
          onConfirm: inviteTeamMembers,
          newMemberCount,
          isBillingMonthly: currentPlan.billing_frequency === 'month',
        })

        return
      }
    }

    inviteTeamMembers()
  }

  const inviteTeamMembers = async () => {
    if (!workspace) {
      showToastMessage('Unable to invite team members.', 'error')
      return
    }

    setIsProcessing(true)

    const failedInvitations: string[] = []

    await Promise.all(
      emailAddressesList.map(async (email) => {
        trackEvent(INVITE_MEMBER_EVENT, profile)

        const res = await client.addMember({
          email: email,
          role: 'User',
          workspaceKey: workspace.key,
          message: message,
          url_invitation: true,
        })

        if (res.type !== SUCCESS_RESPONSE_TYPE) {
          failedInvitations.push(email)
        }
      }),
    )
    setIsProcessing(false)

    if (failedInvitations.length === 0) {
      showToastMessage(`${emailAddressesList.length} invites sent!`, 'success')
      createCardDismissal('home_invite_cta')
    } else {
      showToastMessage(`Couldn't invite the following users: ${failedInvitations.join(', ')}. Try again.`, 'error')
    }

    onClose()
  }

  const handleKeyPress = (e: ReactKeyEvent) => {
    if (e.key === 'Escape') onClose()
  }

  return (
    <>
      <div className={Styles.inputContainer} onKeyDown={handleKeyPress} tabIndex={-1}>
        <div className={Styles.emailInputContainer}>
          <Heading level={3}>Emails</Heading>
          <div className={Styles.pillsWrapper}>
            <PillsInput
              autoFocus
              growOnFocus={false}
              placeholder={
                isMobile ? 'Use tab, space, or comma to separate' : 'Use tab, space, or comma to separate emails'
              }
              className={Styles.emailAddressesListContainer}
              splitOn={[',', ' ']}
              pills={emailAddressesList}
              onChange={(pills: string[] | undefined) => {
                const uniqPills = uniq(pills)
                setEmailAddressesList(uniqPills)
              }}
              validatePill={isEmail}
              plugins={{
                // No-op plugin so we don't load actual mentions plugin
                mentions: new Plugin({}),
              }}
              size="lg"
            />
          </div>
        </div>
        <div className={Styles.messageInputContainer}>
          <Heading level={3}>Message</Heading>
          <TextAreaField
            placeholder="Enter your message..."
            rows={5}
            onChange={(e) => {
              setMessage(e.target.value)
            }}
            value={message}
            wrapperClassName={Styles.emailMessage}
          />
        </div>
      </div>
      <div className={Styles.inviteByEmailFooter}>
        <Button size="lg" variant="tertiary" onPress={onClose}>
          Cancel
        </Button>
        <div className={Styles.inviteByEmailFooterRight}>
          {shouldEnforceSeatLimit && <PricePill plan={nextTierPlan.key} withText />}
          <FreemiumTooltipped label="Upgrade to invite team members">
            <div>
              <Button
                onPress={handleLimitWrappedPress}
                size="lg"
                isDisabled={!showUpgradeToGrowthCta && (!emailAddressesList.length || workspace.is_ap_freemium || isProcessing)}
                endIcon={isMobile ? ArrowRight : undefined}
                frontIcon={showUpgradeToGrowthCta ? LockOpen3 : undefined}
                fullWidth={isMobile}
                className={cx({ [Styles.upgradeToGrowthButton]: showUpgradeToGrowthCta })}
              >
                {showUpgradeToGrowthCta ? 'Upgrade to Growth to Invite Your Team' : 'Invite Members'}
              </Button>
            </div>
          </FreemiumTooltipped>
        </div>
      </div>
    </>
  )
}

export default InviteByEmail
